<template>
  <div 
    @click="openContactDetails"
    :class="[
      'group relative rounded-xl border transition-shadow duration-200 cursor-pointer',
      isMissed
        ? 'bg-red-50 dark:bg-red-900/20 border-red-200/50 dark:border-red-700/30'
        : isAssignedToMe
          ? 'bg-blue-50 dark:bg-blue-900/10 border-blue-200/50 dark:border-blue-700/30 border-l-[3px] border-l-blue-500'
          : 'bg-white dark:bg-slate-800 border-slate-200/50 dark:border-slate-700/50',
      'hover:shadow-md'
    ]"
  >
    <div class="p-3">
      <div class="flex items-center gap-3">
        <!-- Profile Photo or Call Direction Icon -->
        <div class="flex-shrink-0 relative">
          <!-- Profile Photo (for Teams-to-Teams calls with known contact) -->
          <template v-if="contactPhoto">
            <div class="w-10 h-10 rounded-full overflow-hidden ring-2 ring-white dark:ring-slate-700 shadow-sm">
              <img 
                :src="photoSrc" 
                :alt="displayName"
                class="w-full h-full object-cover"
              />
            </div>
            <!-- Small call direction indicator -->
            <div 
              :class="[
                'absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full flex items-center justify-center ring-1 ring-white dark:ring-slate-800',
                isMissed ? 'bg-red-500' : isIncoming ? 'bg-blue-500' : 'bg-emerald-500'
              ]"
            >
              <svg v-if="isMissed" class="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M6 18L18 6M6 6l12 12" />
              </svg>
              <svg v-else-if="isIncoming" class="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
              <svg v-else class="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 10l7-7m0 0l7 7m-7-7v18" />
              </svg>
            </div>
          </template>
          
          <!-- Fallback: Call Direction Icon (for external/phone calls) -->
          <div 
            v-else
            :class="[
              'w-10 h-10 rounded-full flex items-center justify-center',
              callIconClass
            ]"
          >
            <!-- Incoming -->
            <svg v-if="isIncoming && !isMissed" class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l6 6m0-6l-6 6" transform="translate(-3,-1)" />
            </svg>
            <!-- Outgoing -->
            <svg v-else-if="!isIncoming && !isMissed" class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l-6-6m6 0l-6 6" transform="translate(-1,1)" />
            </svg>
            <!-- Missed -->
            <svg v-else class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
        </div>

        <!-- Call Details -->
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2">
            <h3 class="text-sm font-semibold text-slate-900 dark:text-white truncate">
              {{ displayName }}
            </h3>
            <!-- Video call indicator -->
            <svg v-if="isVideo" class="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </div>
          
          <!-- Company or Phone Number -->
          <p class="text-xs text-slate-500 dark:text-slate-400 truncate">
            {{ secondaryInfo }}
          </p>

          <!-- Call Queue indicator -->
          <p v-if="isCallQueueCall" class="text-xs text-purple-600 dark:text-purple-400 mt-0.5">
            📞 {{ callQueueName }}
          </p>

          <!-- Note status badge (for all calls with notes) -->
          <span 
            v-if="call.note_status"
            :class="[
              'inline-block mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium',
              noteStatusClass
            ]"
          >
            {{ noteStatusLabel }}
          </span>

          <!-- Answered by (for group calls and CQ calls) -->
          <p v-if="answeredBy && answeredBy !== displayName" class="text-xs mt-0.5" :class="answeredBy === 'Unbekannt' ? 'text-gray-500 dark:text-gray-400' : isAnsweredByEstimated ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400'">
            {{ isAnsweredByEstimated && answeredBy !== 'Unbekannt' ? 'Vermutlich angenommen von:' : 'Angenommen von:' }} {{ answeredBy }}
          </p>
          
          <!-- Assignment badge (who is handling this call) -->
          <p v-if="call.note_assigned_to_name" class="text-xs mt-0.5 flex items-center gap-1">
            <span 
              :class="[
                'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium',
                isAssignedToMe 
                  ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 ring-1 ring-blue-300 dark:ring-blue-600' 
                  : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'
              ]"
            >
              <svg class="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              {{ isAssignedToMe ? 'Mir zugewiesen' : call.note_assigned_to_name }}
            </span>
          </p>
          
          <!-- Transferred by (for calls transferred to Call Queue) -->
          <p v-if="isTransferred" class="text-xs text-purple-600 dark:text-purple-400 mt-0.5">
            📞 Weitergeleitet von: {{ transferredByName }}
          </p>
          
          <!-- Other participants (for non-CQ group calls only) -->
          <div v-if="!isCallQueueCall && otherParticipants.length > 0" class="flex items-center gap-1 mt-1 flex-wrap">
            <span class="text-xs text-slate-400">Mit:</span>
            <template v-for="(p, idx) in otherParticipants" :key="p.user_id || idx">
              <span 
                v-if="p.user_email && !p.user_email.endsWith('@mailbox-contact.local')"
                @click.stop="openParticipantContact(p.user_email)"
                class="text-xs text-blue-600 dark:text-blue-400 hover:underline cursor-pointer"
              >
                {{ p.user_name }}{{ idx < otherParticipants.length - 1 ? ',' : '' }}
              </span>
              <span 
                v-else
                class="text-xs text-slate-500 dark:text-slate-400"
              >
                {{ p.user_name }}{{ idx < otherParticipants.length - 1 ? ',' : '' }}
              </span>
            </template>
            <span 
              v-if="moreParticipantsCount > 0" 
              @click.stop="showAllParticipants"
              class="text-xs text-blue-500 dark:text-blue-400 hover:underline cursor-pointer"
              title="Alle Teilnehmer anzeigen"
            >
              +{{ moreParticipantsCount }} weitere
            </span>
          </div>
        </div>

        <!-- Time & Duration - fades out on hover to show action buttons -->
        <div class="flex-shrink-0 text-right min-w-[80px] sm:min-w-[100px] sm:group-hover:opacity-0 transition-opacity">
          <p class="text-xs text-slate-500 dark:text-slate-400 leading-tight">
            {{ callTimeInfo.display }}
          </p>
          <p class="text-xs text-slate-400 dark:text-slate-500 leading-tight">
            {{ callTimeInfo.time }}
          </p>
          <p :class="[
            'text-xs font-medium leading-tight',
            isMissed ? 'text-red-600 dark:text-red-400' : 'text-slate-600 dark:text-slate-300'
          ]">
            {{ formattedDuration }}
          </p>
        </div>

        <!-- Action Buttons (on hover desktop, always visible row on mobile) -->
        <div class="hidden sm:flex absolute right-4 top-1/2 -translate-y-1/2 items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <!-- Note/Edit Button -->
          <button
            @click.stop="openNotes"
            :class="[
              'p-2 rounded-full text-white transition-colors shadow-lg',
              hasNote ? 'bg-amber-500 hover:bg-amber-600' : 'bg-slate-400 hover:bg-slate-500'
            ]"
            :title="hasNote ? 'Notiz bearbeiten' : 'Notiz hinzufügen'"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>

          <!-- Callback -->
          <button
            v-if="callbackNumber"
            @click.stop="callback"
            class="p-2 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white transition-colors shadow-lg"
            title="Rückruf"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
          </button>

          <!-- Teams Chat (if email) -->
          <button
            v-if="contactEmail"
            @click.stop="openTeamsChat"
            class="p-2 rounded-full bg-blue-500 hover:bg-blue-600 text-white transition-colors shadow-lg"
            title="Teams Chat"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Mobile action buttons (always visible on small screens) -->
      <div v-if="callbackNumber || contactEmail || true" class="flex sm:hidden items-center gap-2 pt-2 mt-2 border-t border-slate-100 dark:border-slate-700/50">
        <button
          @click.stop="openNotes"
          :class="[
            'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors',
            hasNote ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300' : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'
          ]"
        >
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
          {{ hasNote ? 'Notiz' : 'Notiz' }}
        </button>
        <button
          v-if="callbackNumber"
          @click.stop="callback"
          class="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 transition-colors"
        >
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
          Rückruf
        </button>
        <button
          v-if="contactEmail"
          @click.stop="openTeamsChat"
          class="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 transition-colors"
        >
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          Chat
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted } from 'vue';
import { formatCallDuration, formatCallTime, fetchContactPhoto } from '../websocketService';
import { makePhoneCall } from '../callService';

const props = defineProps({
  call: {
    type: Object,
    required: true
  },
  currentUserId: {
    type: String,
    default: ''
  },
  hasNote: {
    type: Boolean,
    default: false
  },
  isQueueView: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['open-contact', 'show-participants', 'open-notes']);

// Determine if this was an incoming or outgoing call for the current user
const isIncoming = computed(() => {
  // Use direction field from API (set per-user in call_participants)
  if (props.call.direction) {
    return props.call.direction === 'incoming';
  }
  // Fallback: If user is the organizer, it's outgoing
  return props.call.organizer_id !== props.currentUserId;
});

// Photo state - for both incoming and outgoing calls
const contactPhoto = ref(null);

// Computed photo source - handles both Base64 (from API) and Blob URLs (from fetchContactPhoto)
const photoSrc = computed(() => {
  if (!contactPhoto.value) return null;
  // Base64 from API starts with a letter/number, Blob URLs start with "blob:"
  if (contactPhoto.value.startsWith('blob:') || contactPhoto.value.startsWith('http')) {
    return contactPhoto.value;
  }
  // Assume Base64
  return 'data:image/jpeg;base64,' + contactPhoto.value;
});

// Load photo on mount
onMounted(async () => {
  if (isIncoming.value) {
    // For incoming calls: use the cached caller_photo from API
    contactPhoto.value = props.call.caller_photo || null;
  } else {
    // For outgoing calls: fetch the photo of the person we called
    const callee = props.call.participants?.find(p => 
      p.user_id !== props.call.organizer_id && p.role === 'participant' && p.user_email
    );
    if (callee?.user_email && callee?.user_id) {
      const photo = await fetchContactPhoto(callee.user_email, callee.user_id);
      contactPhoto.value = photo;
    }
  }
});

// Determine if this was a missed call for the current user
// API returns is_missed directly on the call object (from call_participants join)
const isMissed = computed(() => {
  // For CQ calls answered by a colleague, don't show as missed (red)
  if (props.call.failure_reason === 'queue_other_answered') {
    return false;
  }
  return props.call.is_missed || false;
});

// Determine if this was a video call
const isVideo = computed(() => props.call.call_type === 'video');

// Check if this call was transferred from another internal user
const isTransferred = computed(() => props.call.is_transferred && props.call.transferred_by_name);

// Get the transferred by name
const transferredByName = computed(() => props.call.transferred_by_name);

// Get the display name (caller/callee or phone number)
const displayName = computed(() => {
  // For incoming calls, show who called
  if (isIncoming.value) {
    // If this is a transferred call, show "Externer Anruf" or the phone number
    // (we don't know the original external caller's identity)
    if (isTransferred.value) {
      // For transferred calls, we only have the phone number
      if (props.call.external_phone) {
        return formatPhoneNumber(props.call.external_phone);
      }
      return 'Externer Anruf (weitergeleitet)';
    }
    
    // Check if organizer was identified (internal Teams user)
    if (props.call.organizer_name) {
      return props.call.organizer_name;
    }
    // Check if external caller was identified via phone lookup
    if (props.call.external_contact_name) {
      return props.call.external_contact_name;
    }
    // Show external phone if available
    if (props.call.external_phone) {
      return formatPhoneNumber(props.call.external_phone);
    }
    return 'Unbekannt';
  }
  
  // For outgoing calls, show who was called
  // For Call Queue calls, show the CQ name (the target you called)
  if (isCallQueueCall.value && callQueueName.value) {
    return callQueueName.value;
  }
  
  // For regular calls, find the first participant that's not the organizer and not a CQ
  const callee = props.call.participants?.find(p => 
    p.user_id !== props.call.organizer_id && 
    p.role === 'participant' &&
    !p.user_email?.match(/^teams-(cq|aa)-/i)
  );
  
  if (callee) {
    return callee.user_name || formatPhoneNumber(callee.phone_number) || 'Unbekannt';
  }
  
  // Fallback: show external contact name or phone
  if (props.call.external_contact_name) {
    return props.call.external_contact_name;
  }
  if (props.call.external_phone) {
    return formatPhoneNumber(props.call.external_phone);
  }
  
  return 'Unbekannt';
});

// Secondary info (company or phone number)
const secondaryInfo = computed(() => {
  if (isIncoming.value) {
    // For identified external callers, show company name
    if (props.call.external_company_name) {
      return props.call.external_company_name;
    }
    // For internal callers, show company
    if (props.call.organizer_company) {
      return props.call.organizer_company;
    }
    // For external calls with identified name, show the phone number
    if ((props.call.organizer_name || props.call.external_contact_name) && props.call.external_phone) {
      return formatPhoneNumber(props.call.external_phone);
    }
    return '';
  }
  
  // For outgoing, show callee's company or phone
  const callee = props.call.participants?.find(p => 
    p.user_id !== props.call.organizer_id && p.role === 'participant'
  );
  
  if (callee?.phone_number) {
    return formatPhoneNumber(callee.phone_number);
  }
  
  return '';
});

// Who answered (for group calls and Call Queue calls)
const answeredBy = computed(() => {
  // For Call Queue calls, show who answered
  if (props.call.call_queue?.answered_by) {
    return props.call.call_queue.answered_by;
  }
  // For regular group calls
  if (!isMissed.value && props.call.first_answerer_name) {
    return props.call.first_answerer_name;
  }
  // answered_by_name from the call record
  if (!isMissed.value && props.call.answered_by_name) {
    return props.call.answered_by_name;
  }
  return null;
});

// Whether the answerer was estimated (not confirmed by Graph API)
const isAnsweredByEstimated = computed(() => {
  return props.call.answered_by_estimated === true;
});

// Check if this is a Call Queue call
const isCallQueueCall = computed(() => {
  return !!props.call.call_queue?.queue_name;
});

// Check if this call is assigned to the current user
const isAssignedToMe = computed(() => {
  return props.call.note_assigned_to_id && props.call.note_assigned_to_id === props.currentUserId;
});

// Get Call Queue name
const callQueueName = computed(() => {
  return props.call.call_queue?.queue_name || null;
});

// Note status styling
const noteStatusClass = computed(() => {
  const statusClasses = {
    'open': 'bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-200',
    'in_progress': 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300',
    'completed': 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300',
    'forwarded': 'bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300'
  };
  return statusClasses[props.call.note_status] || statusClasses['open'];
});

// Note status label
const noteStatusLabel = computed(() => {
  const labels = {
    'open': 'Offen',
    'in_progress': 'In Bearbeitung',
    'completed': 'Erledigt',
    'forwarded': 'Weitergeleitet'
  };
  return labels[props.call.note_status] || props.call.note_status;
});

// Failure reason text for missed calls
const failureReasonText = computed(() => {
  const reason = props.call.failure_reason;
  if (!reason) return null;
  
  const reasonMap = {
    'declined': 'Abgelehnt',
    'busy': 'Besetzt',
    'unanswered': 'Keine Antwort',
    'unreachable': 'Nicht erreichbar',
    'transferred': 'Weitergeleitet',
    'failed': 'Fehlgeschlagen',
    'queue_other_answered': null // Don't show - answered by colleague is already shown
  };
  
  return reasonMap[reason] !== undefined ? reasonMap[reason] : null;
});

// Helper to check if a string looks like a GUID
const isGuidLike = (str) => {
  if (!str) return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);
};

// Get other participants for group calls (excluding current user and organizer)
const otherParticipants = computed(() => {
  if (!props.call.participants || props.call.call_type !== 'groupCall') {
    return [];
  }
  
  return props.call.participants.filter(p => 
    p.user_id !== props.currentUserId && 
    p.role !== 'organizer' &&
    p.user_name &&
    !isGuidLike(p.user_name) &&
    !p.user_email?.startsWith('teams-aa-') &&
    !p.user_email?.startsWith('teams-cq-')
  ).slice(0, 3); // Show max 3
});

// Count of additional participants not shown
const moreParticipantsCount = computed(() => {
  if (!props.call.participants) return 0;
  const total = props.call.participants.filter(p => 
    p.user_id !== props.currentUserId && 
    p.role !== 'organizer' &&
    p.user_name &&
    !isGuidLike(p.user_name) &&
    !p.user_email?.startsWith('teams-aa-') &&
    !p.user_email?.startsWith('teams-cq-')
  ).length;
  return Math.max(0, total - 3);
});

// Participants for Call Queue calls (all real agents with presence)
const cqParticipants = computed(() => {
  if (!props.call.participants || !isCallQueueCall.value) {
    return [];
  }
  
  return props.call.participants.filter(p => 
    p.user_name &&
    p.user_id &&
    !isGuidLike(p.user_name) &&
    !p.user_email?.startsWith('teams-aa-') &&
    !p.user_email?.startsWith('teams-cq-') &&
    p.role !== 'callQueues' &&
    p.role !== 'autoAttendant'
  );
});

// Format duration
const formattedDuration = computed(() => {
  if (isMissed.value) return 'Verpasst';
  return formatCallDuration(props.call.duration_seconds);
});

// Format time - returns object with display, datetime, time, date
const callTimeInfo = computed(() => {
  const info = formatCallTime(props.call.start_time);
  // Handle legacy string format or new object format
  if (typeof info === 'string') {
    return { display: info, datetime: '', time: '', date: '' };
  }
  return info;
});

// Get callback number (phone or email)
const callbackNumber = computed(() => {
  if (isIncoming.value) {
    return props.call.external_phone || props.call.organizer_email;
  }
  
  const callee = props.call.participants?.find(p => 
    p.user_id !== props.call.organizer_id
  );
  return callee?.phone_number || callee?.email;
});

// Get contact email for Teams chat
const contactEmail = computed(() => {
  if (isIncoming.value) {
    const email = props.call.organizer_email;
    // Don't show chat for external phone numbers without email
    return email && !email.endsWith('@mailbox-contact.local') ? email : null;
  }
  
  const callee = props.call.participants?.find(p => 
    p.user_id !== props.call.organizer_id
  );
  const email = callee?.email;
  return email && !email.endsWith('@mailbox-contact.local') ? email : null;
});

// CSS class for call icon background
const callIconClass = computed(() => {
  if (isMissed.value) return 'bg-red-500';
  if (isIncoming.value) return 'bg-blue-500';
  return 'bg-emerald-500';
});

// Format phone number for display
function formatPhoneNumber(phone) {
  if (!phone) return '';
  
  // Remove everything except digits and +
  let cleaned = phone.replace(/[^\d+]/g, '');
  
  // Format German numbers nicely
  if (cleaned.startsWith('+49') || cleaned.startsWith('0049')) {
    const prefix = cleaned.startsWith('+49') ? '+49 ' : '0';
    const rest = cleaned.replace(/^\+49|^0049/, '').replace(/^0/, '');
    
    // Simple formatting: split into groups
    if (rest.length > 6) {
      return prefix + rest.slice(0, 3) + ' ' + rest.slice(3, 6) + ' ' + rest.slice(6);
    }
    return prefix + rest;
  }
  
  // For other numbers, just add some spacing
  if (cleaned.startsWith('0')) {
    return cleaned.replace(/(\d{4})(\d{3,4})(\d+)/, '$1 $2 $3');
  }
  
  return phone;
}

// Callback action
function callback() {
  const number = callbackNumber.value;
  if (!number) return;
  
  // If it's a phone number, use call service (Teams or native)
  if (number.match(/^[\d+\s()-]+$/)) {
    makePhoneCall(number.replace(/[\s()-]/g, ''));
  } else {
    // Otherwise, open Teams call
    window.open(`https://teams.microsoft.com/l/call/0/0?users=${encodeURIComponent(number)}`, '_blank');
  }
}

// Open Teams chat
function openTeamsChat() {
  if (contactEmail.value) {
    window.open(`https://teams.microsoft.com/l/chat/0/0?users=${encodeURIComponent(contactEmail.value)}`, '_blank');
  }
}

// Get the email of the other party (for contact lookup)
const callerEmail = computed(() => {
  if (isIncoming.value) {
    // For incoming calls: use organizer email or external contact email
    return props.call.organizer_email || props.call.external_contact_email || null;
  } else {
    // For outgoing calls: find the callee's email
    const callee = props.call.participants?.find(p => 
      p.user_id !== props.call.organizer_id && p.role === 'participant' && p.user_email
    );
    return callee?.user_email || null;
  }
});

// Open contact details when clicking on the call
function openContactDetails() {
  // Only emit if we have an email to lookup
  const email = callerEmail.value;
  if (email && !email.endsWith('@mailbox-contact.local')) {
    emit('open-contact', email);
  }
}

// Open a participant's contact details
function openParticipantContact(email) {
  if (email && !email.endsWith('@mailbox-contact.local')) {
    emit('open-contact', email);
  }
}

// Show all participants (emit event to parent to show in modal)
function showAllParticipants() {
  // Filter out GUIDs and system accounts
  const filteredParticipants = props.call.participants?.filter(p => 
    p.user_name &&
    !isGuidLike(p.user_name) &&
    !p.user_email?.startsWith('teams-aa-') &&
    !p.user_email?.startsWith('teams-cq-')
  ) || [];
  emit('show-participants', filteredParticipants);
}

// Open notes modal for CQ calls
function openNotes() {
  emit('open-notes', props.call);
}
</script>
